/**
 * CoParent Console — design-system primitives.
 *
 * Single source of truth for shared atoms. Every module must import from
 * here instead of re-implementing eyebrows, cards, pills, banners, etc.
 *
 * Spec: /app/memory/DESIGN_SYSTEM.md  (§ 14 — Component primitives)
 * Tokens: /app/frontend/src/lib/design-tokens.js
 *
 * Components exported:
 *   • Eyebrow        — uppercase tracking label above titles
 *   • SectionHeading — `font-heading text-lg`
 *   • CardTitle      — `font-heading text-base` for in-card headings
 *   • SurfaceCard    — white card + hairline border + rounded-md
 *   • StatusPill     — small inline pill with tone fill + dot
 *   • HeadlineBanner — page-top one-sentence banner with tone tint
 *   • KpiTile        — compact KPI with coloured top stripe + eyebrow + value
 *   • HeroTile       — dominant tone-filled tile with big value + sub
 *   • EmptyState     — calm "nothing here yet" card with optional CTA
 *   • SkeletonRow    — animated muted bar(s) for loading
 *   • InlineError    — small alert-tone error banner
 */
import React from "react";
import { Link } from "react-router-dom";
import { tokens, tone as toneMap, type as typeClasses } from "@/lib/design-tokens";

// ----- helpers -----------------------------------------------------------

const cx = (...parts) => parts.filter(Boolean).join(" ");

/**
 * Resolve a tone name ("positive" / "attention" / "alert" / "info" / "calm"
 * / "school") to its 4-property bundle. Falls back to "calm".
 */
const resolveTone = (t) => toneMap[t] || toneMap.calm;

// ----- Eyebrow -----------------------------------------------------------

export function Eyebrow({ children, className = "", testId }) {
  return (
    <div
      className={cx(typeClasses.eyebrow, className)}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

// ----- SectionHeading ----------------------------------------------------

export function SectionHeading({ as: As = "h2", children, className = "", testId }) {
  return (
    <As
      className={cx(typeClasses.sectionHeading, "mb-3", className)}
      data-testid={testId}
    >
      {children}
    </As>
  );
}

// ----- CardTitle ---------------------------------------------------------

export function CardTitle({ as: As = "h3", children, className = "", testId }) {
  return (
    <As
      className={cx(typeClasses.cardTitle, className)}
      data-testid={testId}
    >
      {children}
    </As>
  );
}

// ----- SurfaceCard -------------------------------------------------------

/**
 * Standard white card with hairline border.
 *
 * Props:
 *   padding   — "none" | "sm" (p-4) | "md" (p-5, default) | "lg" (p-6)
 *   tone      — optional. When set, the border + bg become the tone's
 *               soft fill instead of plain white. Use only when the card
 *               is communicating state (e.g. Outstanding section header).
 *   leftRail  — optional tone name. Renders a 3 px left rail in that
 *               tone's `dot` colour. Used by banking-style rows.
 *   as        — element type (default "div")
 */
export const SurfaceCard = React.forwardRef(function SurfaceCard(
  {
    children,
    className = "",
    padding = "md",
    tone: t,
    leftRail,
    as: As = "div",
    style,
    testId,
    ...rest
  },
  ref,
) {
  const pad =
    padding === "none" ? "" :
    padding === "sm" ? "p-4" :
    padding === "lg" ? "p-6" :
    "p-5";
  const toned = t ? resolveTone(t) : null;
  const mergedStyle = {
    ...(toned
      ? { backgroundColor: toned.bg, borderColor: toned.border }
      : null),
    ...(leftRail
      ? { borderLeft: `3px solid ${resolveTone(leftRail).dot}` }
      : null),
    ...(style || {}),
  };
  return (
    <As
      ref={ref}
      className={cx(
        toned ? "border rounded-md" : "bg-white border border-[#D9E1E3] rounded-md",
        pad,
        className,
      )}
      style={mergedStyle}
      data-testid={testId}
      {...rest}
    >
      {children}
    </As>
  );
});

// ----- StatusPill --------------------------------------------------------

/**
 * Small inline status pill.
 *
 * Props:
 *   tone     — "positive" | "attention" | "alert" | "info" | "calm" | "school"
 *   children — pill label (kept short, e.g. "Outstanding", "Settled")
 *   hideDot  — when true, render without the leading colour dot
 */
export function StatusPill({ tone: t = "calm", hideDot = false, children, className = "", testId }) {
  const c = resolveTone(t);
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-sm border",
        className,
      )}
      style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}
      data-testid={testId}
    >
      {!hideDot ? (
        <span
          className="h-1.5 w-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: c.dot }}
          aria-hidden="true"
        />
      ) : null}
      {children}
    </span>
  );
}

// ----- HeadlineBanner ----------------------------------------------------

/**
 * Single-sentence banner placed near the top of a page or section to
 * answer "what's my situation right now?" in one line.
 */
export function HeadlineBanner({ tone: t = "calm", children, className = "", testId }) {
  const c = resolveTone(t);
  return (
    <div
      className={cx(
        "px-4 py-3 rounded-md text-sm flex items-start gap-2.5",
        className,
      )}
      style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}` }}
      data-testid={testId}
    >
      <span
        className="h-2 w-2 rounded-full flex-shrink-0 mt-1.5"
        style={{ backgroundColor: c.dot }}
        aria-hidden="true"
      />
      <p className="leading-relaxed flex-1">{children}</p>
    </div>
  );
}

// ----- KpiTile -----------------------------------------------------------

/**
 * Compact KPI tile with a 3 px top stripe in the tone's `dot` colour.
 *
 * Props:
 *   label  — uppercase eyebrow (short, ~14 chars)
 *   value  — main numeric or short string (kept ≤14 chars on mobile)
 *   sub    — optional small caption below the value
 *   tone   — drives the top stripe + value colour
 *   href   — optional, when present the tile becomes a Link
 */
export function KpiTile({ label, value, sub, tone: t = "calm", href, className = "", testId }) {
  const c = resolveTone(t);
  const inner = (
    <>
      <span
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ backgroundColor: c.dot }}
        aria-hidden="true"
      />
      <div className={cx(typeClasses.eyebrow, "mt-1")}>{label}</div>
      <div
        className="font-heading text-lg font-semibold tabular-nums mt-1.5 leading-tight"
        style={{ color: c.text }}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value}
      </div>
      {sub ? (
        <div className="text-[11px] text-[#6F828A] mt-1 leading-snug">{sub}</div>
      ) : null}
    </>
  );
  const baseCls = cx(
    "bg-white border border-[#D9E1E3] rounded-md p-3.5 relative overflow-hidden block",
    href ? "hover:bg-[#F4FAFA] transition-colors" : "",
    className,
  );
  if (href) {
    return (
      <Link to={href} className={baseCls} data-testid={testId}>
        {inner}
      </Link>
    );
  }
  return <div className={baseCls} data-testid={testId}>{inner}</div>;
}

// ----- HeroTile ----------------------------------------------------------

/**
 * Dominant tone-filled hero tile. Used at the top of money / status
 * pages where the user's primary number must be unmissable.
 */
export function HeroTile({ label, value, sub, secondary, tone: t = "positive", className = "", testId }) {
  const c = resolveTone(t);
  return (
    <div
      className={cx("border rounded-md p-5", className)}
      style={{ backgroundColor: c.bg, borderColor: c.border }}
      data-testid={testId}
    >
      <div className={typeClasses.eyebrow} style={{ color: c.text }}>
        {label}
      </div>
      <div
        className="font-heading text-3xl sm:text-4xl font-semibold tabular-nums mt-2 leading-none"
        style={{ color: c.text }}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value}
      </div>
      {sub ? (
        <div className="text-xs mt-2" style={{ color: c.text }}>
          {sub}
        </div>
      ) : null}
      {secondary ? (
        <div
          className="mt-3 pt-3 border-t text-xs flex items-center justify-between"
          style={{ borderColor: c.border, color: c.text }}
        >
          <span>{secondary.label}</span>
          <span className="font-mono-data tabular-nums font-medium">
            {secondary.value}
          </span>
        </div>
      ) : null}
    </div>
  );
}

// ----- EmptyState --------------------------------------------------------

/**
 * Calm "nothing here yet" card.
 *
 * Props:
 *   icon     — optional lucide-react icon component (e.g. WalletIcon)
 *   title    — one-line headline ("No expenses yet.")
 *   body     — optional secondary line of conversational copy
 *   cta      — optional { label, href, onClick } primary action
 */
export function EmptyState({ icon: Icon, title, body, cta, className = "", testId }) {
  return (
    <div
      className={cx(
        "border border-[#D9E1E3] bg-white rounded-md p-6 text-center",
        className,
      )}
      data-testid={testId}
    >
      {Icon ? (
        <Icon
          className="w-6 h-6 mx-auto mb-3 text-[#9DB0B5]"
          strokeWidth={1.5}
        />
      ) : null}
      <p className="text-sm text-[#2E3A3F] font-medium">{title}</p>
      {body ? (
        <p className="text-xs text-[#6F828A] mt-1.5 leading-relaxed max-w-sm mx-auto">
          {body}
        </p>
      ) : null}
      {cta ? (
        cta.href ? (
          <Link
            to={cta.href}
            className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 rounded-sm bg-[#1F9FA4] hover:bg-[#178085] text-white text-xs font-medium transition-colors"
            data-testid={testId ? `${testId}-cta` : undefined}
          >
            {cta.label} →
          </Link>
        ) : (
          <button
            type="button"
            onClick={cta.onClick}
            className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 rounded-sm bg-[#1F9FA4] hover:bg-[#178085] text-white text-xs font-medium transition-colors"
            data-testid={testId ? `${testId}-cta` : undefined}
          >
            {cta.label} →
          </button>
        )
      ) : null}
    </div>
  );
}

// ----- SkeletonRow -------------------------------------------------------

/**
 * Loading placeholder. Sized to roughly match the real content so the
 * layout doesn't jump when data arrives.
 *
 * Props:
 *   lines   — number of stacked bars to render (default 1)
 *   width   — Tailwind width class for the bar(s), default "w-full"
 *   height  — Tailwind height class, default "h-4"
 */
export function SkeletonRow({ lines = 1, width = "w-full", height = "h-4", className = "", testId }) {
  const rows = Array.from({ length: Math.max(1, lines) });
  return (
    <div className={cx("space-y-2", className)} data-testid={testId}>
      {rows.map((_, i) => (
        <div
          key={i}
          className={cx(height, width, "bg-[#EEF3F4] rounded-sm animate-pulse")}
        />
      ))}
    </div>
  );
}

// ----- InlineError -------------------------------------------------------

/**
 * Small alert-tone banner placed inline near the source of the problem
 * (form field, list-load failure, etc.).
 */
export function InlineError({ children, className = "", testId }) {
  return (
    <div
      className={cx(
        "border border-[#E8B6AF] bg-[#FBE6E3] text-[#8C1F12] rounded-md px-3 py-2 text-sm",
        className,
      )}
      role="alert"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

// ----- default export ----------------------------------------------------

const primitives = {
  Eyebrow,
  SectionHeading,
  CardTitle,
  SurfaceCard,
  StatusPill,
  HeadlineBanner,
  KpiTile,
  HeroTile,
  EmptyState,
  SkeletonRow,
  InlineError,
  tokens,
};

export { tokens };
export default primitives;
